import memoize from "memoizee";
import { fetchAuthSession } from "aws-amplify/auth";
import { I18n as AmplifyI18n } from "aws-amplify/utils";
import i18n from "../../i18n";
import config from "../../config";
// api
import {
  getUser,
  getUserIgnoreCache,
  getUsers,
  updateUser,
} from "@nualang/nualang-api-and-queries/APIs/Users";
import {
  invalidateCache,
  DataType,
} from "@nualang/nualang-api-and-queries/APIs/invalidation";

export const getUserDetails = async (userId) => {
  try {
    const response = await getUser(userId, true);
    if (response.errorMessage) {
      throw new Error(response.errorMessage);
    }
    return response;
  } catch (error) {
    return false;
  }
};

export const memoizeGetUserDetails = memoize(getUserDetails, { promise: true });

// The signed-in user's own record. Skips the API Gateway cache (an hour on
// prod), so a change to their customer, like blocking community content,
// applies on their next page load. Every query for the own user should use
// this: they share one query key, so any of them can be the one that fetches.
export const getOwnUser = (userId) => getUserIgnoreCache(userId, false);

export const handleChangeLanguage = (language) => {
  const code = config.forLanguages[language].Code;
  i18n.changeLanguage(code);
  AmplifyI18n.setLanguage(code);
};

export const fetchMemberDetails = async (member) => {
  const { memberId } = member;
  const userDetails = await memoizeGetUserDetails(memberId, true);
  const userImage =
    userDetails && userDetails.UserAttributes
      ? userDetails.UserAttributes.picture
      : null;
  const username =
    userDetails && userDetails.UserAttributes
      ? userDetails.UserAttributes.preferred_username
      : null;
  return {
    ...member,
    userImage,
    username,
  };
};

export const fetchMembersDetails = async (members) => {
  if (!members.length) {
    return [];
  }
  const memberIds = [];
  members.forEach((m) => memberIds.push(m.memberId));

  const response = await getUsers(
    {
      ids: memberIds.join(","),
      limit: config.limits.users,
    },
    true,
  );
  const dynamoUsers = response?.Users || [];

  // A membership record can outlive the user it points at (deleted account), so
  // an unmatched memberId renders with empty details rather than failing the list.
  const formatted = members.map((member) => {
    const foundUser = dynamoUsers.find((u) => u.Username === member.memberId);
    const userAttributes = foundUser?.Attributes || {};
    return {
      ...member,
      userImage: userAttributes.picture || "",
      username: userAttributes.preferred_username || "",
    };
  });

  return formatted;
};

/**
 * Resolves a display label for each collaborator entry. Collaborators may be
 * stored as an email (added via the UI) or a Cognito username/userId (added
 * server-side for first-time SSO users, e.g. "Wayside-SAML_<id>"). Emails are
 * left as-is; userIds are resolved to the user's preferred_username so they
 * render as a friendly name rather than the raw id. Returns a map of
 * { [collaborator]: label }; only entries that need a different label are
 * included. Never throws — unresolved ids fall back to the raw value.
 */
export const fetchCollaboratorLabels = async (collaborators = []) => {
  const userIds = collaborators.filter((c) => c && !c.includes("@"));
  if (userIds.length === 0) {
    return {};
  }

  const labels = {};
  try {
    const response = await getUsers(
      {
        ids: userIds.join(","),
        limit: config.limits.users,
      },
      true,
    );
    const dynamoUsers = response?.Users || [];
    userIds.forEach((id) => {
      const foundUser = dynamoUsers.find((u) => u.Username === id);
      labels[id] = foundUser?.Attributes?.preferred_username || id;
    });
  } catch (error) {
    // On failure, fall back to showing the raw id for every entry.
    userIds.forEach((id) => {
      labels[id] = id;
    });
  }

  return labels;
};

export const updateUserAttributes = async (username, attributes) => {
  await updateUser(username, attributes);

  const { tokens: signInUserSession } = await fetchAuthSession({
    forceRefresh: true,
  });

  invalidateCache(DataType.USER, {
    userId: username,
  });

  return {
    username,
    signInUserSession,
  };
};
